import { CopyIcon, EditIcon, MoreIcon, PlusIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { ReactElement, useMemo } from 'react';
import styled from 'styled-components';

import {
  ColumnProps,
  DEFAULT_FIELD_SCHEMA,
  FieldSchemaEditorSupportedTypes,
  getAllFieldKeys,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor';
import { Identifier } from 'dashboard/models/identifier';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { FieldSchemaType } from 'lib/fields';

const MenuIconContainer = styled.span<{ danger?: boolean }>`
  margin-right: 5px;
  position: relative;
  top: 3px;
`;

interface OverflowMenuItemMeta {
  filter: () => boolean;
  id: string;
  action: () => void;
  item: string;
  icon: ReactElement;
  variant?: 'basic' | 'danger';
}

export interface OverflowMenuProps {
  canBeDeleted?: boolean;
  deleteFromActiveFieldSchema: (key: string) => void;
  draftFieldKeys: string[];
  fieldSchemas: FieldSchemaEditorSupportedTypes[];
  draftIdentifiers?: Identifier[];
  includeDeactivateAction?: boolean;
  setInactiveFieldKey: (key: string) => void;
}

const OverflowMenu = ({
  canBeDeleted,
  deleteFromActiveFieldSchema,
  draftFieldKeys,
  field,
  fieldSchemas,
  draftIdentifiers = [],
  includeDeactivateAction = true,
  insertSibling,
  prefix,
  setInactiveFieldKey,
  setFocusedSchema,
  updateSelf,
}: OverflowMenuProps & ColumnProps) => {
  const preexistingFieldKeys = getAllFieldKeys(fieldSchemas);
  const getNextAvailableKey = (draftFieldKeys: string[], baseKey: string) => {
    // If baseKey_copy doesn't exist, return it
    if (!draftFieldKeys.includes(`${baseKey}_copy`)) {
      return { key: `${baseKey}_copy`, count: 0 };
    }

    let i = 1;
    // Loop to find the next available key
    while (draftFieldKeys.includes(`${baseKey}_copy_${i}`)) {
      i++;
    }

    // Return the constructed unique key
    return { key: `${baseKey}_copy_${i}`, count: i };
  };

  const menuItemMeta = useMemo<OverflowMenuItemMeta[]>(() => {
    return [
      {
        filter: () => {
          return !preexistingFieldKeys.includes(prefix);
        },
        id: 'edit-field',
        action: () => {
          setFocusedSchema(prefix, 'edit');
        },
        item: 'Edit field',
        icon: <EditIcon />,
        variant: 'basic',
      },
      {
        filter: () => {
          return (
            field.type === FieldSchemaType.Hash ||
            (field.type === FieldSchemaType.Array &&
              field.config['item-schema']?.type === FieldSchemaType.Hash)
          );
        },
        id: 'add-field',
        action: () => {
          if (field.type === FieldSchemaType.Hash) {
            field.config['item-schemas'] = [...field.config['item-schemas'], DEFAULT_FIELD_SCHEMA];
            setFocusedSchema(`${prefix}.${field.config['item-schemas'].length - 1}`, 'add');
          }
          if (
            field.type === FieldSchemaType.Array &&
            field.config['item-schema']?.type === FieldSchemaType.Hash
          ) {
            const originalItemSchemas = field.config['item-schema']?.config['item-schemas'];
            field.config['item-schema'].config['item-schemas'] = [
              ...originalItemSchemas,
              DEFAULT_FIELD_SCHEMA,
            ];
            setFocusedSchema(`${prefix}.[].${originalItemSchemas.length}`, 'add');
          }
          updateSelf(field);
        },
        icon: <PlusIcon />,
        item: 'Add field',
        variant: 'basic',
      },
      {
        filter: () => {
          return !!field.key;
        },
        id: 'duplicate',
        action: () => {
          const { key, count } = getNextAvailableKey(draftFieldKeys, field.key);
          const previousIdentifierConfig = draftIdentifiers.find(
            (identifier) => identifier.key === field.key,
          );
          const identifierConfig = previousIdentifierConfig
            ? ({
                ...previousIdentifierConfig,
                key: key,
              } as Identifier)
            : undefined;
          if (field.type === RelationSchemaType.RelationSchema) {
            if (field['inverse-key']) {
              // bi-directional relation
              const { key: inverseKey, count: inverseCount } = getNextAvailableKey(
                draftFieldKeys,
                field['inverse-key'],
              );

              insertSibling(
                {
                  ...field,
                  key: key,
                  label: `${field.label} Copy ${count ? count : ''}`,
                  cardinality: field.cardinality,
                  'inverse-key': inverseKey,
                  'inverse-label': `${field['inverse-label']} Copy ${
                    inverseCount ? inverseCount : ''
                  }`,
                  'inverse-cardinality': field['inverse-cardinality'],
                },
                identifierConfig,
              );
            } else {
              // uni-directional relation
              insertSibling(
                {
                  ...field,
                  key: key,
                  label: `${field.label} Copy ${count ? count : ''}`,
                  cardinality: field.cardinality,
                },
                identifierConfig,
              );
            }
          } else {
            insertSibling(
              {
                ...field,
                key: key,
                label: `${field.label} Copy ${count ? count : ''}`,
              },
              identifierConfig,
            );
          }
        },
        item: 'Duplicate',
        icon: <CopyIcon />,
        variant: 'basic',
      },
      {
        filter: () => {
          return (
            includeDeactivateAction &&
            fieldSchemas // only the top level fields are deactivate-able
              .map((fieldSchema) => fieldSchema.key)
              .includes(prefix) &&
            preexistingFieldKeys.includes(prefix)
          );
        },
        id: 'deactivate',
        action: () => {
          setInactiveFieldKey(prefix);
        },
        item: 'Deactivate',
        icon: <TrashIcon />,
        variant: 'danger',
      },
      {
        filter: () => {
          return !preexistingFieldKeys.includes(prefix) && !!canBeDeleted;
        },
        id: 'delete',
        action: () => {
          deleteFromActiveFieldSchema(prefix);
          setFocusedSchema('', 'edit');
        },
        item: 'Delete',
        icon: <TrashIcon />,
        variant: 'danger',
      },
    ];
  }, [
    canBeDeleted,
    deleteFromActiveFieldSchema,
    draftFieldKeys,
    field,
    fieldSchemas,
    includeDeactivateAction,
    insertSibling,
    preexistingFieldKeys,
    prefix,
    setInactiveFieldKey,
    setFocusedSchema,
    updateSelf,
  ]);

  return (
    <DropdownMenu
      trigger={
        <Button
          aria-label={`More actions for ${field.key ? field.key : 'unnamed field'}`}
          id={`${prefix}-menu`}
          size='sm'
          variant='inverse'
          width='icon'
          onClick={(e) => {
            // Avoid clicking table row
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <MoreIcon />
        </Button>
      }
    >
      {menuItemMeta
        .filter((element) => element.filter())
        .map((element) => (
          <DropdownMenu.Item
            id={`${prefix}-menu-${element.id}`}
            key={`${prefix}-menu-${element.id}`}
            variant={element.variant}
            onClick={(e) => {
              // Avoid clicking table row
              e.preventDefault();
              e.stopPropagation();
              element.action();
            }}
            onMouseOver={(e) => {
              // Don't let the mouseover event bubble up to the table row
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MenuIconContainer>{element.icon}</MenuIconContainer>
            {element.item}
          </DropdownMenu.Item>
        ))}
    </DropdownMenu>
  );
};

export default OverflowMenu;
